import axios from "axios";
import config from "../config";
import store from "../store";
import { updateToken, logout } from "../store/authUserSlice";

const api = axios.create({
  baseURL: `${config.apiUrl}`,
  withCredentials: true,
  timeout: 600000, // 10 minutes timeout
});

// Flag to avoid multiple refresh requests
let isRefreshing = false;
let refreshSubscribers = [];

function onRefreshed(newAccessToken) {
  refreshSubscribers.forEach((callback) => callback(newAccessToken));
  refreshSubscribers = [];
}

function subscribeTokenRefresh(callback) {
  refreshSubscribers.push(callback);
}

api.interceptors.request.use((req) => {
  const state = store.getState();
  // SECURITY: Get token from Redux state only (memory), never from localStorage
  const token = state.authUser?.userInfo?.token;
  if (token) {
    req.headers.Authorization = `Bearer ${token}`;
  }
  // Identify requests originating from the Ensaras portal frontend.
  // The backend uses this to bypass API-endpoint restrictions (APIAccess)
  // which are only meant for direct API consumers.
  req.headers['X-Ensaras-Client'] = 'portal';
  return req;
});

// Response interceptor for auto-refresh
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response && error.response.status === 403) {
      const { message } = error.response.data;
      if (message === "Unauthorized" || message === "Token has expired") {
        store.dispatch(logout());
        localStorage.clear();
        localStorage.clear(); // Clean up any legacy storage
        window.location.href = "/signin";
      }
      return Promise.reject(error);
    }

    // Handle 401 - attempt refresh
    if (error.response && error.response.status === 401 && error.response?.data?.code === "token_not_valid" && !originalRequest._retry) {
      originalRequest._retry = true;

      if (isRefreshing) {
        return new Promise((resolve) => {
          subscribeTokenRefresh((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            resolve(api(originalRequest));
          });
        });
      }

      isRefreshing = true;

      try {
        // Call Django JWT refresh endpoint
        // FIXED: Correct endpoint is /api/refresh/ (not /api/token/refresh/)
        const response = await axios.post(
          `${config.apiUrl}/refresh/`,
          {}, // No body needed, refresh_token is in httpOnly cookie
          { withCredentials: true }
        );

        // FIXED: Backend returns { token: "..." } (not { access: "..." })
        const newAccessToken = response.data.token;

        // SECURITY: Store token in Redux state only, never in localStorage
        store.dispatch(updateToken(newAccessToken));
        api.defaults.headers.common["Authorization"] = `Bearer ${newAccessToken}`;
        onRefreshed(newAccessToken);

        // Retry the original request with new token
        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
        return api(originalRequest);
      } catch (refreshError) {
        store.dispatch(logout());
        localStorage.clear();
        localStorage.clear(); // Clean up any legacy storage
        window.location.href = "/signin";
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }
    if (error.response && error.response.status === 401) {
      store.dispatch(logout());
      localStorage.clear();
      localStorage.clear(); // Clean up any legacy storage
      // Avoid redirection on same page
      const current = window.location.pathname;
      if (current !== "/signin") {
        window.location.href = "/signin";
      }
      return Promise.reject(error);
    }

    return Promise.reject(error);
  }
);

export default api;
